// src/components/ui/index.js
// Primitivi condivisi. Prima di questo file la stessa cosa era reimplementata
// a mano su ogni pagina: 5 header con 3 glifi diversi per "indietro", 8 empty
// state con 4 misure di titolo, lo spinner con override inline 22 volte.
//
// Regole: inline styles + token da styles/tokens (no CSS modules, no Tailwind),
// niente business logic qui dentro, niente fetch.

import React, { useEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { C } from '../../styles/tokens'

// ── useScrollLock ────────────────────────────────────────────────────────
// Blocca lo scroll della pagina dietro un overlay. Chiamalo dentro il
// componente del modale: si sblocca da solo allo smontaggio.
//
// C'erano 12 overlay e solo 3 lo facevano. Negli altri 9, scorrendo sopra un
// modale aperto si muoveva la pagina sotto — su mobile è il difetto che fa
// sembrare l'app rotta. Salva e ripristina il valore precedente invece di
// azzerarlo, così due overlay annidati non si pestano i piedi.
export function useScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])
}

// ── PageHeader ───────────────────────────────────────────────────────────
// [indietro] [titolo + sottotitolo] [slot destro]
// `size`: 'md' per le pagine di dettaglio, 'lg' per quelle di primo livello.
// Il glifo indietro è sempre ArrowLeft: '‹' e '←' erano due font diversi che
// cambiavano peso e allineamento da una pagina all'altra.
export function PageHeader({ title, subtitle, onBack, right, size = 'md', style }) {
  const titleSize = size === 'lg' ? 26 : 20
  return (
    <header style={{ padding: '1.25rem 1rem 0.75rem', display: 'flex', alignItems: 'center', gap: 12, ...style }}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Indietro"
          style={{
            width: 36, height: 36, flexShrink: 0, borderRadius: 10,
            border: 'none', background: 'none', color: C.text2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', padding: 0, marginLeft: -6,
          }}
        >
          <ArrowLeft size={22} strokeWidth={2} />
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Due righe al massimo: un titolo lungo su tre righe spinge giù
            tutta la pagina e il pulsante indietro va a spasso. */}
        <h1 style={{
          margin: 0, fontFamily: 'var(--font-display)', fontSize: titleSize,
          fontWeight: 900, letterSpacing: '-0.5px', lineHeight: 1.15, color: C.text,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {title}
        </h1>
        {subtitle && (
          <div style={{ fontSize: 12, color: C.text2, marginTop: 3 }}>{subtitle}</div>
        )}
      </div>
      {right}
    </header>
  )
}

// ── SectionLabel ─────────────────────────────────────────────────────────
// L'etichetta maiuscola sopra un gruppo di card. Era ripetuta inline 63 volte.
export function SectionLabel({ children, right, style }) {
  const label = (
    <span style={{
      fontSize: 10, fontWeight: 800, color: C.text2,
      letterSpacing: '0.12em', textTransform: 'uppercase',
    }}>
      {children}
    </span>
  )
  if (!right) return <div style={{ marginBottom: 10, ...style }}>{label}</div>
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, ...style }}>
      {label}
      {right}
    </div>
  )
}

// ── Tabs ─────────────────────────────────────────────────────────────────
// `items` è [[chiave, etichetta], …]. Il markup `.pill-tabs` era ricopiato
// identico in 4 pagine, sempre senza semantica: uno screen reader leggeva
// quattro bottoni sciolti invece di un gruppo di schede con una attiva.
export function Tabs({ items, value, onChange, style }) {
  return (
    <div className="pill-tabs" role="tablist" style={style}>
      {items.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={value === key}
          onClick={() => onChange(key)}
          className={`pill-tab ${value === key ? 'active' : ''}`}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// ── Spinner ──────────────────────────────────────────────────────────────
// La classe .spinner sta in index.css (l'animazione serve un keyframe CSS).
// Qui ci sono solo le due varianti che i call-site riscrivevano a mano:
// la misura, e il giro bianco quando gira dentro un bottone pieno.
//
// `onFill` cambia TUTTO l'anello, non solo il bordo superiore: i call-site
// riscrivevano solo `borderTopColor: '#fff'` e lasciavano il resto del
// cerchio sul grigio di --border2, che sul rosso pieno spariva.
export function Spinner({ size = 20, onFill = false, style }) {
  return (
    <div
      className="spinner"
      role="status"
      aria-label="Caricamento"
      style={{
        width: size,
        height: size,
        // Lati separati e non `borderColor`: React avvisa se si mescola una
        // shorthand con la sua versione puntuale nello stesso stile.
        ...(onFill && {
          borderRightColor: 'rgba(255,255,255,0.35)',
          borderBottomColor: 'rgba(255,255,255,0.35)',
          borderLeftColor: 'rgba(255,255,255,0.35)',
          borderTopColor: '#fff',
        }),
        ...style,
      }}
    />
  )
}

// Spinner centrato che tiene il posto del contenuto mentre carica.
export function LoadingBlock({ padding = 40 }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: `${padding}px 0` }}>
      <Spinner size={28} />
    </div>
  )
}

// ── StatRow ──────────────────────────────────────────────────────────────
// Fila di 2-4 numeri con etichetta sotto, divisi da un filetto.
// `cells` è [{ value, label, color?, small? }, …].
//
// Ce n'erano tre versioni quasi identiche (Hyrox 18px, Peso 20px, Corsa
// inline) che divergevano solo per dimensione e colore: affiancate nella
// stessa app sembravano tre componenti diversi senza motivo.
export function StatRow({ cells, style }) {
  return (
    <div style={{
      display: 'flex', background: 'var(--bg3)',
      border: `1px solid ${C.border1}`, borderRadius: 16, overflow: 'hidden',
      ...style,
    }}>
      {cells.map((c, i) => (
        <div key={c.label ?? i} style={{
          flex: 1, minWidth: 0, padding: '12px 6px', textAlign: 'center',
          borderRight: i < cells.length - 1 ? `1px solid ${C.border1}` : 'none',
        }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 900, lineHeight: 1,
            fontSize: c.small ? 13 : 19, color: c.color || C.text,
            // I valori testuali (es. "Wall Balls") sforano: meglio troncare
            // che mandare a capo e sfalsare l'altezza delle celle vicine.
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {c.value}
          </div>
          <div style={{
            fontSize: 9, fontWeight: 700, color: C.text2, marginTop: 5,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {c.label}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── EmptyState ───────────────────────────────────────────────────────────
// `icon` è un elemento lucide già dimensionato, es. <Footprints size={44} />.
// `action` è { label, onClick }: la CTA non è opzionale per caso — degli 8
// empty state che c'erano prima solo 3 ce l'avevano, e gli altri 5 erano
// esattamente i punti in cui un utente nuovo si ferma senza sapere che fare.
export function EmptyState({ icon, title, description, action, style }) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem 1.25rem', ...style }}>
      {icon && (
        <div style={{ display: 'flex', justifyContent: 'center', color: C.textFaintDecor, marginBottom: 14 }}>
          {icon}
        </div>
      )}
      <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>{title}</div>
      {description && (
        <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.45, maxWidth: 300, margin: '0 auto' }}>
          {description}
        </div>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            marginTop: 18, padding: '11px 20px', borderRadius: 12, border: 'none',
            background: C.accentFill, color: '#fff', fontSize: 13, fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}
